<!-- src/views/org-dashboard/settings/OrgSettingsEvaluate.vue -->
<template>
  <div class="evaluate-settings p-base">
    
    <!-- Header with Save Button -->
    <div class="flex items-left justify-between mb-8">
      <div>
        <h3 class="text-[28px] leading-tight font-semibold text-[#2c3e50] mb-2">Evaluation Metrics</h3>
        <p class="text-[#666] text-base">Configure how talent is assessed across different trajectories.</p>
      </div>
      <vs-button 
        @click="saveConfigurationToServer" 
        color="#f5bb00" 
        type="filled" 
        class="px-8 py-2.5 shadow-md hover:shadow-lg transform active:scale-95 transition-all duration-200"
      >
        <span class="font-bold text-white text-base">Save Changes</span>
      </vs-button>
    </div>

    <!-- Metric Type Switcher (Appraisal, Fitment, etc.) -->
    <vs-row>
      <vs-col class="w-full">
        <div class="my-4 text-white flex justify-center">
          <div class="flex gap-3">
            <vs-button 
              class="hover:bg-primary hover:text-white px-6 rounded-full" 
              v-for="(config, key) in configTypes"
              :key="key" 
              @click="selectedConfigType = key"
              :type="selectedConfigType === key ? 'filled' : 'border'"
              :color="selectedConfigType === key ? 'primary' : 'gray'"
            >
              {{ config.title }}
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>

    <!-- Main Content Area: Categories and Criteria -->
    <vs-row>
      <vs-col class="w-full">
        <!-- NEW: Total Weight Display -->
        <div v-if="initialWeightTotals[selectedConfigType] !== undefined"
          class="text-right font-semibold mb-4 text-lg p-4 bg-gray-50 rounded-lg border border-gray-200">
          <span>Total Weight: </span>
          <span :class="isCurrentConfigWeightValid ? 'text-green-600' : 'text-red-500'">
            {{ currentConfigTotal }} / {{ initialWeightTotals[selectedConfigType] }}
          </span>
          <p v-if="!isCurrentConfigWeightValid" class="text-sm text-red-500 font-normal mt-1">
            Please adjust weights to match the required total of {{ initialWeightTotals[selectedConfigType] }}.
          </p>
        </div>

        <div class="space-y-8 mt-2">
          <div v-for="(criteria, categoryName) in getCurrentConfig()" :key="categoryName" class="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
            
            <h3 class="text-sm font-bold mb-6 text-center text-[#2c3e50] uppercase tracking-wider">
              {{ categoryName || 'Special Factors' }}
            </h3>

            <div class="space-y-4">
              <div v-for="(criterion, index) in criteria" :key="index" class="criterion-item border-b border-gray-100 last:border-b-0 py-3">
                <vs-row vs-align="flex-start" vs-type="flex" vs-justify="space-between" class="w-full">
                  
                  <!-- Left Column: Title, Key, Description -->
                  <vs-col vs-w="8">
                    <div class="text-left pr-4">
                      <div class="flex items-baseline gap-2 mb-1">
                        <h4 class="font-medium text-base text-[#2c3e50] uppercase">{{ criterion.title }}</h4>
                        <span class="text-xs text-gray-400">({{ criterion.key }})</span>
                      </div>
                      <p v-if="criterion.desc" class="text-sm text-gray-500 italic">{{ criterion.desc }}</p>
                    </div>
                  </vs-col>

                  <!-- Right Column: Controls -->
                  <vs-col vs-w="4">
                    <div class="flex items-center justify-end gap-2 h-full">
                      
                      <span class="text-sm text-gray-500">Weight:</span>
                      <vs-input type="number" v-model="criterion.value" class="w-20" />
                      <span class="text-sm">%</span>

                      <vx-tooltip text="Edit Criteria" position="top">
                        <vs-button type="flat" color="primary" icon-pack="feather" icon="icon-edit-2" class="w-8 h-8 rounded-lg hover:bg-primary-light ml-2" @click="openEditCriterionModal(categoryName, index)" />
                      </vx-tooltip>
                      
                      <vx-tooltip text="Delete Criteria" position="top">
                        <vs-button type="flat" color="danger" icon="delete" class="w-8 h-8 rounded-lg hover:bg-danger-light" @click="removeCriterion(categoryName, index)" />
                      </vx-tooltip>

                    </div>
                  </vs-col>
                </vs-row>
              </div>

              <vs-button type="border" color="primary" class="w-full mt-4 rounded-full border-yellow-400 hover:bg-yellow-50" @click="openCriterionModal(categoryName)">
                + Add Criteria
              </vs-button>
            </div>
          </div>
        </div>
      </vs-col>
    </vs-row>

    <!-- ... (Add Category Button and other Modals are unchanged) ... -->
    <vs-row class="mt-10 mb-10">
      <vs-col class="w-full flex items-center justify-center">
        <vs-button type="border" class="custom-category-btn" @click="openCategoryModal">
          <i class="material-icons text-[16px]">folder</i>
          <span class="ml-1">+ Add New Category</span>
        </vs-button>
      </vs-col>
    </vs-row>
    <vs-popup class="popup-card" v-model="showCategoryModal" :active.sync="showCategoryModal" :button-close-hidden="true">
      <div class="p-6 relative">
        <div class="absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors" @click="closeCategoryModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </div>
        <h3 class="text-xl font-semibold mb-6 text-[#2c3e50]">Add New Category</h3>
        <vs-input v-model="newCategory" label="Category Name" placeholder="Enter category name" class="w-full mb-6" @keyup.enter="addCategory" />
        <div class="flex justify-end gap-3">
          <vs-button type="border" color="dark" @click="closeCategoryModal">Cancel</vs-button>
          <vs-button color="primary" @click="addCategory">Add Category</vs-button>
        </div>
      </div>
    </vs-popup>
    <vs-popup class="popup-card" v-model="showCriteriaModal" :active.sync="showCriteriaModal" :button-close-hidden="true">
      <div class="p-6 relative">
        <div class="absolute top-4 right-4 cursor-pointer text-gray-400 hover:text-gray-600 transition-colors" @click="closeCriterionModal">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </div>
        <h3 class="text-xl font-semibold mb-6 text-[#2c3e50]">{{ isEditing ? 'Edit Criteria' : 'Add New Criteria' }}</h3>
        <vs-input v-model="newCriteria.title" label="Title" placeholder="Enter criteria title" class="w-full mb-4" />
        <vs-input v-model="newCriteria.key" label="Key" placeholder="Enter criteria key (e.g., KP)" class="w-full mb-4" />
        <vs-input v-model="newCriteria.desc" label="Description" placeholder="Enter criteria description" class="w-full mb-6" />
        <div class="flex justify-end gap-3">
          <vs-button type="border" color="dark" @click="closeCriterionModal">Cancel</vs-button>
          <vs-button color="primary" @click="isEditing ? updateCriterion() : addCriterion()">
            {{ isEditing ? 'Save Changes' : 'Add Criteria' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <vs-popup class="popup-card" :active.sync="showUnsavedChangesPopup" :button-close-hidden="true">
      <!-- Custom Header with close button matching screenshot -->
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
        <h3 class="text-[16px] font-bold text-[#2c3e50]">Unsaved Changes</h3>
        <button @click="showUnsavedChangesPopup = false" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Main Content Area -->
      <div class="p-6 text-left">
        
        <!-- Warning Alert Box matching the image -->
        <div class="bg-[#FFF7E6] rounded-lg p-4 flex items-start gap-3 mb-6">
          <div class="text-[#FF9F43] mt-0.5">
            <!-- Warning Triangle Icon -->
            <svg class="w-[20px] h-[20px]" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div>
            <h4 class="text-[#FF9F43] font-bold text-[14px] mb-1">Unsaved modifications</h4>
            <p class="text-[#FF9F43] text-[13px] font-medium">You have metric changes that are currently unsaved.</p>
          </div>
        </div>

        <!-- Descriptive Text -->
        <p class="text-[#666] text-[14px] mb-4 leading-relaxed">
          Leaving this tab will discard all recent weight adjustments and configuration changes made to the evaluation metrics.
        </p>
        <p class="text-[#2c3e50] font-bold text-[14px] mb-8">
          Do you want to save these changes before leaving?
        </p>

        <!-- Right-aligned Action Buttons -->
        <div class="flex items-center justify-end gap-3">
          <vs-button 
            type="border" 
            color="dark" 
            class="px-5 py-2 text-sm font-medium" 
            @click="handleDiscardAndLeave">
            Discard
          </vs-button>
          
          <vs-button 
            color="#f5bb00" 
            type="filled"
            class="px-6 py-2 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all" 
            @click="handleSaveAndLeave">
            Save & Leave
          </vs-button>
        </div>
        
      </div>
    </vs-popup>
  </div>
</template>

// <script>
// Deep-clone utility to prevent reference issues
import { cloneDeep } from 'lodash';

export default {
  data() {
    return {
      kvdrConfigId: null,
      isDefaultConfig: false,
      fullFetchedConfig: null,
      isDirty: false,
      pendingTab: null,
      originalConfigurations: null,
      showUnsavedChangesPopup: false,
      nextRoute: null,
      isLeaving: false,

      selectedConfigType: 'appraisal',
      showCriteriaModal: false,
      showCategoryModal: false,
      isEditing: false,
      selectedCategory: '',
      newCategory: '',
      editingCriterion: { category: null, index: -1 },
      newCriteria: { title: '', key: '', desc: '', value: '10' },

      initialWeightTotals: {
        appraisal: 100,
        fitment: 100,
        succession: 100,
        performance: 100
      },

      configTypes: {
        appraisal: { title: 'Appraisal' },
        fitment: { title: 'Fitment' },
        succession: { title: 'Succession' },
        performance: { title: 'Performance' }
      },
      configurations: {
        appraisal: {}, fitment: {}, succession: {}, performance: {}
      }
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    },
    currentConfigTotal() {
      const currentConfig = this.configurations[this.selectedConfigType];
      if (!currentConfig) return 0;
      return Object.values(currentConfig)
        .flat()
        .reduce((sum, criterion) => sum + (Number(criterion.value) || 0), 0);
    },
    isCurrentConfigWeightValid() {
      const initialTotal = this.initialWeightTotals[this.selectedConfigType];
      if (initialTotal === undefined) return true;
      return this.currentConfigTotal === initialTotal;
    },
  },
  watch: {
    orgData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          this.loadKvdrConfig();
        }
      }
    },
    configurations: {
      deep: true,
      handler(newVal) {
        if (!this.originalConfigurations) return;
        this.isDirty = JSON.stringify(newVal) !== JSON.stringify(this.originalConfigurations);
        
        // Emit this status to the Parent Component so it knows we have unsaved changes
        this.$emit('dirty-state-changed', this.isDirty);
      }
    }
  },

  // --- NEW: Lifecycle hooks to handle BROWSER navigation ---
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  
  methods: {
    handleBeforeUnload(event) {
      if (!this.isDirty) return;
      event.preventDefault();
      event.returnValue = ''; 
    },

    // NEW: The parent will call this method when the user tries to change tabs
    triggerUnsavedPopup(targetTab) {
      this.pendingTab = targetTab;
      this.showUnsavedChangesPopup = true;
    },

    async handleSaveAndLeave() {
      const saved = await this.saveConfigurationToServer();
      if (saved) {
        this.showUnsavedChangesPopup = false;
        this.isDirty = false;
        this.$emit('dirty-state-changed', false);
        
        // Tell parent to proceed with the tab change
        if (this.pendingTab) {
          this.$emit('proceed-to-tab', this.pendingTab);
        }
      }
    },

    handleDiscardAndLeave() {
      // Revert everything back to how it was
      this.configurations = cloneDeep(this.originalConfigurations);
      this.isDirty = false;
      this.$emit('dirty-state-changed', false);
      this.showUnsavedChangesPopup = false;

      // Tell parent to proceed with the tab change
      if (this.pendingTab) {
        this.$emit('proceed-to-tab', this.pendingTab);
      }
    },

    // ... (rest of your methods are unchanged)
    normalizeConfigWeights(configData, targetTotal) {
      if (!configData || Object.keys(configData).length === 0) return configData;
      const allCriteria = Object.values(configData).flat();
      if (allCriteria.length === 0) return configData;
      const currentTotal = allCriteria.reduce((sum, c) => sum + (Number(c.value) || 0), 0);
      if (currentTotal === targetTotal || currentTotal === 0) return configData;
      const scalingFactor = targetTotal / currentTotal;
      let runningTotal = 0;
      for (let i = 0; i < allCriteria.length - 1; i++) {
        const criterion = allCriteria[i];
        const scaledValue = Math.round((Number(criterion.value) || 0) * scalingFactor);
        criterion.value = scaledValue.toString();
        runningTotal += scaledValue;
      }
      const lastCriterion = allCriteria[allCriteria.length - 1];
      lastCriterion.value = (targetTotal - runningTotal).toString();
      return configData;
    },
    async loadKvdrConfig() {
      if (!this.orgData || !this.orgData._id) return;
      this.$vs.loading();
      try {
        const token = localStorage.getItem('accessToken');
        const API_BASE_URL = "https://api.getboarded.tech";
        const response = await fetch(`${API_BASE_URL}/kvdr-org-config?orgId=${this.orgData._id}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const json = await response.json();
        if (json.data && json.data.length > 0) {
          const configDoc = json.data[0];
          this.fullFetchedConfig = { ...configDoc };
          this.isDefaultConfig = configDoc.orgId !== this.orgData._id;
          this.kvdrConfigId = this.isDefaultConfig ? null : configDoc._id;

          // Special rule for default Appraisal data
          if (this.isDefaultConfig && configDoc.appraisalData) {
            const appraisal = configDoc.appraisalData;
            const allCriteria = Object.values(appraisal).flat();
            if (allCriteria.length >= 3) {
              allCriteria[0].value = "15"; // STRENGTHS
              allCriteria[1].value = "15"; // LEARNING
              allCriteria[2].value = "15"; // APPLICATION
            }
            this.configurations.appraisal = appraisal;
          } else if (configDoc.appraisalData) {
            this.configurations.appraisal = this.normalizeConfigWeights(configDoc.appraisalData, 100);
          }
          this.configurations.fitment = this.normalizeConfigWeights(configDoc.fitmentData || {}, 100);
          this.configurations.succession = this.normalizeConfigWeights(configDoc.successionData || {}, 100);
          this.configurations.performance = this.normalizeConfigWeights(configDoc.performanceData || {}, 100);
          this.originalConfigurations = cloneDeep(this.configurations);
        }
      } catch (err) {
        console.error("Error loading KVDR config:", err);
        this.$vs.notify({ title: 'Error', text: 'Could not load evaluation metrics.', color: 'danger' });
      } finally {
        this.$vs.loading.close();
      }
    },
    async saveConfigurationToServer() {
      for (const key in this.configurations) {
        const currentTotal = Object.values(this.configurations[key]).flat().reduce((sum, c) => sum + (Number(c.value) || 0), 0);
        const targetTotal = this.initialWeightTotals[key];
        if (currentTotal !== targetTotal) {
          this.$vs.notify({
            title: 'Invalid Weights',
            text: `The total weight for '${this.configTypes[key].title}' must be ${targetTotal}. It is currently ${currentTotal}.`,
            color: 'danger',
            time: 6000
          });
          return false;
        }
      }
      try {
        this.$vs.loading();
        const apiToken = localStorage.getItem('accessToken');
        const API_BASE_URL = "https://api.getboarded.tech";
        const metricsPayload = {
          appraisalData: this.configurations.appraisal,
          fitmentData: this.configurations.fitment,
          successionData: this.configurations.succession,
          performanceData: this.configurations.performance
        };
        let response;
        if (this.kvdrConfigId && !this.isDefaultConfig) {
          response = await fetch(`${API_BASE_URL}/kvdr-org-config/${this.kvdrConfigId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiToken}` },
            body: JSON.stringify(metricsPayload)
          });
        } else {
          const postPayload = { ...this.fullFetchedConfig, ...metricsPayload, orgId: this.orgData._id };
          delete postPayload._id;
          response = await fetch(`${API_BASE_URL}/kvdr-org-config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiToken}` },
            body: JSON.stringify(postPayload)
          });
        }
        if (!response.ok) throw new Error("Backend failed to save settings");
        const responseData = await response.json();
        if (!this.kvdrConfigId && responseData && (responseData._id || responseData.data?._id)) {
          this.kvdrConfigId = responseData._id || responseData.data._id;
          this.isDefaultConfig = false;
        }
        this.$vs.notify({ title: 'Success', text: 'Evaluation Metrics saved successfully!', color: 'success' });
        
        this.$store.commit('organization/UPDATE_EVALUATION_METRICS', metricsPayload);
        try {
          const pathParts = window.location.pathname.split('/');
          const slugIndex = pathParts.indexOf('organization') + 1;
          const slug = pathParts[slugIndex];
          if (slug) {
            let localData = {};
            const existingStr = localStorage.getItem(`onboardingConfigs_${slug}`);
            if (existingStr) {
                localData = JSON.parse(existingStr);
            }
            localData.appraisal = this.configurations.appraisal;
            localData.fitment = this.configurations.fitment;
            localData.succession = this.configurations.succession;
            localData.performance = this.configurations.performance;
            localStorage.setItem(`onboardingConfigs_${slug}`, JSON.stringify(localData));
          }
        } catch (e) {
            console.error("Failed to sync to local storage", e);
        }

        this.originalConfigurations = cloneDeep(this.configurations);
        return true;
      } catch (error) {
        console.error("Save Error:", error);
        this.$vs.notify({ title: 'Error', text: 'Unable to save metrics. Please try again.', color: 'danger' });
        return false;
      } finally {
        this.$vs.loading.close();
      }
    },
    getCurrentConfig() {
      return this.configurations[this.selectedConfigType] || {};
    },
    openCategoryModal() {
      this.newCategory = ''; this.showCategoryModal = true;
    },
    closeCategoryModal() {
      this.showCategoryModal = false; this.newCategory = '';
    },
    addCategory() {
      if (this.newCategory.trim()) {
        const uppercaseCat = this.newCategory.toUpperCase();
        if (!this.configurations[this.selectedConfigType][uppercaseCat]) {
          this.$set(this.configurations[this.selectedConfigType], uppercaseCat, []);
        }
        this.closeCategoryModal();
      }
    },
    openCriterionModal(categoryName) {
      this.isEditing = false;
      this.selectedCategory = categoryName;
      this.newCriteria = { title: '', key: '', desc: '', value: '10' };
      this.showCriteriaModal = true;
    },
    openEditCriterionModal(category, index) {
      this.isEditing = true;
      this.editingCriterion = { category, index };
      const targetItem = this.configurations[this.selectedConfigType][category][index];
      this.newCriteria = cloneDeep(targetItem);
      this.showCriteriaModal = true;
    },
    closeCriterionModal() {
      this.showCriteriaModal = false;
      this.isEditing = false;
      this.selectedCategory = '';
      this.editingCriterion = { category: null, index: -1 };
    },
    addCriterion() {
      if (this.newCriteria.title && this.newCriteria.key) {
        if (!this.configurations[this.selectedConfigType][this.selectedCategory]) {
           this.$set(this.configurations[this.selectedConfigType], this.selectedCategory, []);
        }
        this.configurations[this.selectedConfigType][this.selectedCategory].push({ ...this.newCriteria });
        this.closeCriterionModal();
      }
    },
    updateCriterion() {
      if (this.newCriteria.title && this.newCriteria.key) {
        const { category, index } = this.editingCriterion;
        if (category && this.configurations[this.selectedConfigType][category]) {
          this.configurations[this.selectedConfigType][category].splice(index, 1, { ...this.newCriteria });
        }
        this.closeCriterionModal();
      }
    },
    removeCriterion(categoryName, index) {
      this.configurations[this.selectedConfigType][categoryName].splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
/* Styles are unchanged */
.criterion-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;

  &:hover {
    border-color: #e5e7eb;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
}
.popup-card {
  >>>.vs-popup--header { display: none !important; }
  >>>.vs-popup { width: 500px !important; }
}
.con-vs-popup>>>.vs-popup--content { padding: 0 !important; margin: 0 !important; overflow-x: hidden !important; width: auto !important; border-radius: 8px !important; background: white !important; }
.con-vs-popup>>>.vs-popup { background: white !important; border-radius: 8px !important; }
.vs-input>>>.vs-input--input { box-shadow: none; }
.vs-input>>>.vs-input--placeholder { top: 5px; }
.custom-category-btn {
  border: 1px solid #f5bb00 !important;
  color: #f5bb00 !important;
  background: transparent !important;
  border-radius: 999px !important;
  padding: 6px 16px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
}
.custom-category-btn:hover { background: #fff7cc !important; }
.custom-category-btn .material-icons { font-size: 16px !important; }
</style>